
// pages/LoginPage.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import API from '../api';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AccountStatusModal from '../components/AccountStatusModal';
import '../styles/global.css';

export default function LoginPage() {
  // ✅ NEW: Login mode toggle
  const [loginMode, setLoginMode] = useState('admin'); // 'admin' or 'staff'
  
  const [email, setEmail] = useState('');
  const [pan, setPan] = useState('');
  const [password, setPassword] = useState('');
  const [staffId, setStaffId] = useState(''); // ✅ NEW: Staff ID
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [lockoutTime, setLockoutTime] = useState(null);
  const [touched, setTouched] = useState({});
  const [mounted, setMounted] = useState(false);
  const [focusedField, setFocusedField] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const [statusModal, setStatusModal] = useState({
    show: false,
    type: '',
    message: '',
    trialInfo: null
  });

  const [trialInfo, setTrialInfo] = useState(null);

  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    setMounted(true);
  }, []);

  // ✅ Validation based on login mode
  const emailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()), [email]);
  const panValid = useMemo(() => /^\d{9}$/.test(pan.trim()), [pan]);
  const staffIdValid = useMemo(() => staffId.trim().length >= 6, [staffId]);
  
  const formValid = loginMode === 'admin' 
    ? (emailValid && panValid && password.length > 0)
    : (staffIdValid && password.length > 0);

  const touch = (field) => setTouched((t) => ({ ...t, [field]: true }));

  // ✅ Reset form when switching modes
  useEffect(() => {
    setEmail('');
    setPan('');
    setStaffId('');
    setPassword('');
    setError('');
    setTouched({});
  }, [loginMode]);

  // Lockout Logic
  useEffect(() => {
    const lockout = localStorage.getItem('loginLockout');
    if (lockout) {
      const lockoutData = JSON.parse(lockout);
      const now = Date.now();
      if (now < lockoutData.until) {
        setLockoutTime(lockoutData.until);
      } else {
        localStorage.removeItem('loginLockout');
        setAttempts(0);
      }
    }
  }, []);

  useEffect(() => {
    if (lockoutTime) {
      const interval = setInterval(() => {
        const remaining = lockoutTime - Date.now();
        if (remaining <= 0) {
          setLockoutTime(null);
          localStorage.removeItem('loginLockout');
          setAttempts(0);
          setError('');
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [lockoutTime]);

  const getRemainingTime = () => {
    if (!lockoutTime) return '';
    const remaining = Math.ceil((lockoutTime - Date.now()) / 1000);
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getStatusType = (code, accountStatus) => {
    switch (code) {
      case 'ACCOUNT_EXPIRED': return 'expired';
      case 'ACCOUNT_INACTIVE': return 'inactive';
      case 'ACCOUNT_SUSPENDED': return 'suspended';
      case 'TRIAL_EXPIRED': return 'trial_expired';
      case 'STAFF_INACTIVE': return 'inactive';
      default:
        if (accountStatus === 'expired') return 'expired';
        if (accountStatus === 'inactive') return 'inactive';
        if (accountStatus === 'suspended') return 'suspended';
        return 'expired';
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (loginMode === 'admin') {
      setTouched({ email: true, pan: true, password: true });
    } else {
      setTouched({ staffId: true, password: true });
    }

    if (lockoutTime && Date.now() < lockoutTime) {
      setError(`Too many failed attempts. Try again in ${getRemainingTime()}`);
      return;
    }

    if (!formValid) {
      setError('Please fill in all fields correctly');
      return;
    }

    setLoading(true);

    try {
      let response;
      
      if (loginMode === 'admin') {
        // ✅ Admin login
        response = await API.post('/users/login', {
          email: email.trim().toLowerCase(),
          pan: pan.trim(),
          password
        });
      } else {
        // ✅ Staff login
        response = await API.post('/users/staff-login', {
          staffId: staffId.trim().toUpperCase(),
          password
        });
      }

      const { data } = response;

      if (!data.token) throw new Error('Login failed');

      localStorage.removeItem('loginLockout');
      setAttempts(0);

      // Use AuthContext login
      await login(data.user, data.token, data.trial);

      if (data.trial) {
        setTrialInfo(data.trial);
      }

      setSuccessMessage(data.message || 'Login successful!');

      if (data.message) {
        toast.success(data.message);
      }

      setShowSuccess(true);
      setLoading(false);
      
      // Navigate after success animation
      setTimeout(() => {
        let destination = '/dashboard';
        if (data.user.role === 'superadmin') {
          destination = '/admin/dashboard';
        } else if (data.user.role === 'admin') {
          destination = '/dashboard';
        } else if (data.user.role === 'staff') {
          destination = '/dashboard';
        }
        
        navigate(destination, { replace: true });
      }, 1500);

    } catch (err) {
      setLoading(false);
      
      
      const errorData = err.response?.data;
      const errorMessage = errorData?.message || 'Login failed.';
      const errorCode = errorData?.code;
      const accountStatus = errorData?.accountStatus;
      
      if (['ACCOUNT_EXPIRED', 'ACCOUNT_INACTIVE', 'ACCOUNT_SUSPENDED', 'TRIAL_EXPIRED', 'STAFF_INACTIVE', 'SUBSCRIPTION_EXPIRED'].includes(errorCode)) {
        setStatusModal({
          show: true,
          type: getStatusType(errorCode, accountStatus),
          message: errorMessage,
          trialInfo: errorData?.trial || null
        });
        return;
      } else {
        setError(errorMessage);
        toast.error(errorMessage);
        
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);

        if (newAttempts >= 5) {
          const lockUntil = Date.now() + (5 * 60 * 1000);
          localStorage.setItem('loginLockout', JSON.stringify({ until: lockUntil }));
          setLockoutTime(lockUntil);
          setError('Account locked for 5 minutes.');
          toast.error('Account locked for 5 minutes due to too many failed attempts.');
        }
        setTimeout(() => setError(''), 5000);
      }
    }
  };

  const isLocked = lockoutTime && Date.now() < lockoutTime;

  const handleModalClose = () => {
    setStatusModal({ show: false, type: '', message: '', trialInfo: null });
  };

  const handleSubscribe = () => {
    navigate('/subscription');
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@pharmatrack.com?subject=Account Support Request';
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        
        .page-container {
          min-height: 100vh;
          display: flex;
          font-family: 'Poppins', sans-serif;
          background-color: #1a2b3c;
          background-image: radial-gradient(circle at 10% 20%, rgba(4, 159, 137, 0.1) 0%, transparent 20%),
                            radial-gradient(circle at 90% 80%, rgba(4, 159, 137, 0.1) 0%, transparent 20%);
          overflow: hidden;
          position: relative;
        }

        .mobile-header { display: none; }

        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
        @keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        @keyframes checkmark { 0% { transform: scale(0); } 50% { transform: scale(1.2); } 100% { transform: scale(1); } }

        .form-input::placeholder { color: rgba(255,255,255,0.4); }
        
        input:-webkit-autofill,
        input:-webkit-autofill:hover, 
        input:-webkit-autofill:focus {
          -webkit-text-fill-color: white;
          -webkit-box-shadow: 0 0 0px 1000px #233446 inset;
          transition: background-color 5000s ease-in-out 0s;
        }

        .login-toggle {
          display: flex;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 12px;
          padding: 4px;
          margin-bottom: 25px;
        }

        .toggle-btn {
          flex: 1;
          padding: 12px 20px;
          border: none;
          background: transparent;
          color: #a0aec0;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border-radius: 10px;
          transition: all 0.3s ease;
        }

        .toggle-btn.active {
          background: linear-gradient(135deg, #81e6d9 0%, #4fd1c5 100%);
          color: #1a202c;
        }

        .toggle-btn:hover:not(.active) {
          background: rgba(255,255,255,0.1);
        }

        @media (max-width: 900px) {
          .page-container {
            flex-direction: column;
            overflow-y: auto;
          }
          .left-panel { display: none !important; }
          .right-panel { 
            width: 100% !important; 
            padding: 20px !important;
            display: flex;
            flex-direction: column;
            justify-content: flex-start !important;
            padding-top: 40px !important;
          }
          .mobile-header {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            margin-bottom: 30px;
            width: 100%;
          }
          .form-card {
            margin-bottom: 40px;
          }
        }
      `}</style>

      <div className="page-container">
        
        {/* LEFT PANEL - Desktop Only */}
        <div className="left-panel" style={styles.leftPanel}>
          <div style={styles.illustrationContainer}>
            <img 
              src="https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/People/Technologist.png" 
              alt="3D Login Illustration" 
              style={styles.heroImage}
            />
            
            <div style={styles.heroTextContainer}>
              <div style={styles.logoBadge}>💊 PharmaTrack</div>
              <h1 style={styles.heroTitle}>Manage Your Pharmacy<br/>With Precision</h1>
              <p style={styles.heroSubtitle}>
                Real-time ledger tracking, inventory analytics, and seamless payments all in one place.
              </p>
            </div>
            
            <div style={styles.bgCircle1} />
            <div style={styles.bgCircle2} />
          </div>
        </div>

        {/* RIGHT PANEL - Form & Mobile Header */}
        <div className="right-panel" style={styles.rightPanel}>
          
          {/* MOBILE HEADER */}
          <div className="mobile-header">
             <img 
               src="https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/People/Technologist.png" 
               alt="Logo" 
               style={{ width: '80px', marginBottom: '15px' }} 
             />
             <div style={{ color: '#81e6d9', fontWeight: 'bold', fontSize: '24px', marginBottom: '5px' }}>
                PharmaTrack
             </div>
             <p style={{ color: '#a0aec0', fontSize: '14px', margin: 0 }}>
                Manage your pharmacy on the go.
             </p>
          </div>

          {/* SUCCESS OVERLAY */}
          {showSuccess && (
            <div style={styles.successOverlay}>
              <div style={styles.successCard}>
                <div style={styles.checkIcon}>✓</div>
                <h3 style={styles.successTitle}>
                  {successMessage || 'Welcome Back!'}
                </h3>
                <p style={styles.successSubtitle}>Redirecting you now...</p>
                
                {trialInfo && trialInfo.isTrialUser && !trialInfo.isExpired && (
                  <div style={styles.trialBadge}>
                    🎁 {trialInfo.daysRemaining} day{trialInfo.daysRemaining !== 1 ? 's' : ''} left in your free trial
                  </div>
                )}
                
                {trialInfo && !trialInfo.isTrialUser && (
                  <div style={styles.activeBadge}>
                    ✨ Active Subscription
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{
            ...styles.formCard,
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
          }} className="form-card">
            
            <div style={styles.headerSection}>
              <h2 style={styles.headerTitle}>Login</h2>
              <div style={styles.headerLine} />
              <p style={styles.headerSubtitle}>Welcome onboard with us!</p>
            </div>

            {/* ✅ LOGIN MODE TOGGLE */}
            <div className="login-toggle">
              <button 
                type="button"
                className={`toggle-btn ${loginMode === 'admin' ? 'active' : ''}`}
                onClick={() => setLoginMode('admin')}
              >
                👤 Admin Login
              </button>
              <button 
                type="button"
                className={`toggle-btn ${loginMode === 'staff' ? 'active' : ''}`}
                onClick={() => setLoginMode('staff')}
              >
                👥 Staff Login
              </button>
            </div>

            {/* ERROR MESSAGE */}
            {error && (
              <div style={styles.errorBox}>
                <span>⚠️</span> {error}
                {attempts > 0 && attempts < 5 && (
                  <span style={{ fontSize: 11, marginLeft: 5 }}>
                    ({attempts}/5 attempts)
                  </span>
                )}
              </div>
            )}

            {/* LOCKOUT WARNING */}
            {isLocked && (
              <div style={styles.warningBox}>
                <span>🔒 Account locked. Try again in {getRemainingTime()}</span>
              </div>
            )}

            <form onSubmit={submit} style={{ marginTop: 15 }}>
              
              {/* ✅ ADMIN LOGIN FIELDS */}
              {loginMode === 'admin' && (
                <>
                  {/* EMAIL INPUT */}
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Email Address</label>
                    <div style={styles.inputWrapper(focusedField === 'email', touched.email && !emailValid)}>
                      <input
                        type="email"
                        className="form-input"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(''); }}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => { setFocusedField(''); touch('email'); }}
                        disabled={isLocked}
                        style={styles.input}
                      />
                    </div>
                    {touched.email && !emailValid && (
                      <span style={styles.fieldError}>Please enter a valid email</span>
                    )}
                  </div>

                  {/* PAN INPUT */}
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Pharmacy PAN</label>
                    <div style={styles.inputWrapper(focusedField === 'pan', touched.pan && !panValid)}>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="9-digit PAN"
                        value={pan}
                        onChange={(e) => { setPan(e.target.value.replace(/\D/g, '').slice(0, 9)); setError(''); }}
                        onFocus={() => setFocusedField('pan')}
                        onBlur={() => { setFocusedField(''); touch('pan'); }}
                        disabled={isLocked}
                        style={styles.input}
                      />
                    </div>
                    {touched.pan && !panValid && (
                      <span style={styles.fieldError}>PAN must be 9 digits</span>
                    )}
                  </div>
                </>
              )}

              {/* ✅ STAFF LOGIN FIELDS */}
              {loginMode === 'staff' && (
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Staff ID</label>
                  <div style={styles.inputWrapper(focusedField === 'staffId', touched.staffId && !staffIdValid)}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter your Staff ID (e.g., 1234-STF-001)"
                      value={staffId}
                      onChange={(e) => { setStaffId(e.target.value.toUpperCase()); setError(''); }}
                      onFocus={() => setFocusedField('staffId')}
                      onBlur={() => { setFocusedField(''); touch('staffId'); }}
                      disabled={isLocked}
                      style={styles.input}
                    />
                  </div>
                  {touched.staffId && !staffIdValid && (
                    <span style={styles.fieldError}>Please enter a valid Staff ID</span>
                  )}
                  <p style={{ color: '#718096', fontSize: '11px', marginTop: '5px' }}>
                    💡 Staff ID is provided by your pharmacy admin
                  </p>
                </div>
              )}

              {/* PASSWORD INPUT */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Password</label>
                <div style={styles.inputWrapper(focusedField === 'password', touched.password && !password)}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => { setFocusedField(''); touch('password'); }}
                    disabled={isLocked}
                    style={styles.input}
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)} 
                    style={styles.eyeBtn}
                    tabIndex={-1}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                {touched.password && !password && (
                  <span style={styles.fieldError}>Password is required</span>
                )}
                {loginMode === 'admin' && (
                  <div style={styles.forgotPass}>
                    <Link to="/forgot-password" style={{ color: '#a0aec0', textDecoration: 'none', fontSize: 12 }}>
                      Forgot Password?
                    </Link>
                  </div>
                )}
              </div>

              {/* SUBMIT BUTTON */}
              <button 
                type="submit" 
                disabled={loading || !formValid || isLocked}
                style={styles.submitBtn(loading || !formValid || isLocked)}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                    <span style={styles.spinner}></span>
                    LOGGING IN...
                  </span>
                ) : (
                  loginMode === 'admin' ? 'LOGIN AS ADMIN' : 'LOGIN AS STAFF'
                )}
              </button>

              {/* REGISTER LINK - Only for admin */}
              {loginMode === 'admin' && (
                <div style={styles.footerLink}>
                  New to PharmaTrack?{' '}
                  <Link to="/register" style={{ color: '#81e6d9', fontWeight: 600, textDecoration: 'none' }}>
                    Register Here
                  </Link>
                </div>
              )}

              {/* Staff info */}
              {loginMode === 'staff' && (
                <div style={styles.footerLink}>
                  Need access?{' '}
                  <span style={{ color: '#81e6d9' }}>Contact your pharmacy admin</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* ACCOUNT STATUS MODAL */}
      {statusModal.show && (
        <AccountStatusModal
          type={statusModal.type}
          message={statusModal.message}
          trialInfo={statusModal.trialInfo}
          onClose={handleModalClose}
          onSubscribe={handleSubscribe}
          onContactSupport={handleContactSupport}
        />
      )}
    </>
  );
}

const styles = {
  leftPanel: {
    width: '55%',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  rightPanel: {
    width: '35%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 2,
  },
  illustrationContainer: {
    width: '100%',
    maxWidth: '600px',
    padding: '40px',
    position: 'relative',
    textAlign: 'center',
  },
  heroImage: {
    width: '320px',
    height: 'auto',
    animation: 'float 6s ease-in-out infinite',
    marginBottom: '30px',
    filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.5))',
    objectFit: 'contain',
  },
  heroTextContainer: {
    animation: 'slideUp 0.8s ease-out',
  },
  logoBadge: {
    display: 'inline-block',
    padding: '8px 16px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '20px',
    color: '#81e6d9',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '20px',
    backdropFilter: 'blur(5px)',
  },
  heroTitle: {
    color: '#fff',
    fontSize: '36px',
    fontWeight: '700',
    marginBottom: '15px',
    lineHeight: 1.2,
  },
  heroSubtitle: {
    color: '#a0aec0',
    fontSize: '16px',
    lineHeight: 1.6,
    maxWidth: '400px',
    margin: '0 auto',
  },
  bgCircle1: {
    position: 'absolute',
    top: '-50px',
    left: '50px',
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.1), transparent)',
    backdropFilter: 'blur(20px)',
    zIndex: -1,
  },
  bgCircle2: {
    position: 'absolute',
    bottom: '100px',
    right: '50px',
    width: '150px',
    height: '150px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(129, 230, 217, 0.1), transparent)',
    backdropFilter: 'blur(20px)',
    zIndex: -1,
  },
  formCard: {
    width: '100%',
    maxWidth: '420px',
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '30px',
    padding: '45px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    transition: 'all 0.5s ease',
  },
  headerSection: {
    marginBottom: '20px',
  },
  headerTitle: {
    color: '#fff',
    fontSize: '32px',
    fontWeight: '600',
    margin: 0,
    letterSpacing: '0.5px',
  },
  headerLine: {
    width: '40px',
    height: '4px',
    background: '#81e6d9',
    borderRadius: '2px',
    marginTop: '5px',
    marginBottom: '10px',
  },
  headerSubtitle: {
    color: '#a0aec0',
    fontSize: '16px',
    fontWeight: '400',
  },
  inputGroup: {
    marginBottom: '20px',
  },
  label: {
    color: '#cbd5e0',
    fontSize: '13px',
    marginBottom: '8px',
    display: 'block',
    fontWeight: '500',
  },
  inputWrapper: (focused, error) => ({
    background: 'rgba(0, 0, 0, 0.25)',
    borderRadius: '12px',
    border: error ? '1px solid #e53e3e' : focused ? '1px solid #81e6d9' : '1px solid rgba(255, 255, 255, 0.05)',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.3s ease',
  }),
  input: {
    width: '100%',
    padding: '16px',
    background: 'transparent',
    border: 'none',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
  },
  eyeBtn: {
    background: 'transparent',
    border: 'none',
    color: '#a0aec0',
    padding: '0 15px',
    cursor: 'pointer',
    fontSize: '16px',
  },
  forgotPass: {
    textAlign: 'right',
    marginTop: '8px',
  },
  fieldError: {
    color: '#fc8181',
    fontSize: '11px',
    marginTop: '5px',
    display: 'block',
  },
  submitBtn: (disabled) => ({
    width: '100%',
    padding: '16px',
    marginTop: '25px',
    background: disabled ? '#4a5568' : 'linear-gradient(135deg, #81e6d9 0%, #4fd1c5 100%)',
    color: disabled ? '#a0aec0' : '#1a202c',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    letterSpacing: '1px',
    boxShadow: disabled ? 'none' : '0 4px 14px 0 rgba(129, 230, 217, 0.39)',
  }),
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid #1a202c',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  footerLink: {
    textAlign: 'center',
    marginTop: '25px',
    fontSize: '13px',
    color: '#a0aec0',
  },
  errorBox: {
    background: 'rgba(229, 62, 62, 0.2)',
    color: '#feb2b2',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: '1px solid rgba(229, 62, 62, 0.3)',
  },
  warningBox: {
    background: 'rgba(236, 201, 75, 0.2)',
    color: '#fbd38d',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '15px',
    border: '1px solid rgba(236, 201, 75, 0.3)',
  },
  successOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(26, 43, 60, 0.95)',
    zIndex: 10,
    borderRadius: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(10px)',
  },
  successCard: {
    textAlign: 'center',
    color: 'white',
    animation: 'slideUp 0.5s ease',
    padding: '20px',
  },
  checkIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #81e6d9 0%, #4fd1c5 100%)',
    color: '#1a202c',
    fontSize: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    animation: 'checkmark 0.5s ease',
    boxShadow: '0 4px 20px rgba(129, 230, 217, 0.4)',
  },
  successTitle: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#fff',
  },
  successSubtitle: {
    fontSize: '14px',
    color: '#a0aec0',
    marginBottom: '15px',
  },
  trialBadge: {
    marginTop: '15px',
    padding: '12px 20px',
    background: 'rgba(129, 230, 217, 0.2)',
    borderRadius: '10px',
    fontSize: '13px',
    color: '#81e6d9',
    border: '1px solid rgba(129, 230, 217, 0.3)',
  },
  activeBadge: {
    marginTop: '15px',
    padding: '12px 20px',
    background: 'rgba(72, 187, 120, 0.2)',
    borderRadius: '10px',
    fontSize: '13px',
    color: '#68d391',
    border: '1px solid rgba(72, 187, 120, 0.3)',
  },
};